import {
    LiveblocksErrorBoundary,
    RoomProvider,
    subscribeLiveblocksAvailability,
    useMyPresence,
    useRoom,
} from "@configs/liveblocks.config"
import { useEffect, useRef, useState } from "react"

import { useCollaborationColorSync } from "@hooks/useCollaborationColors"
import useTabActivity from "@hooks/useTabActivity"
import useUser from "@hooks/useUser"

import {
    CollaborationRoomProps,
    ConnectedCollaborationRoomProps,
    EditingLockKeepAliveProbeProps,
    RoomConnectionControllerProps,
} from "./CollaborationRoom.types"

// Disconnect an idle or backgrounded tab so it stops billing collaboration minutes.
const HIDDEN_TAB_GRACE_MS = 30 * 1000
const IDLE_TIMEOUT_MS = 5 * 60 * 1000

// After Liveblocks reports unavailable, retry the connection this many times, this far apart,
// before giving up for the rest of the session (10 attempts over ~50 minutes).
const MAX_RECONNECT_ATTEMPTS = 10
const RECONNECT_INTERVAL_MS = 5 * 60 * 1000

/**
 * Mount the presence-color writer once per room.
 *
 * Kept private to this module: it exists only so every room gets exactly one
 * `useCollaborationColorSync` subscription by construction, regardless of which
 * surface opened the room.
 */
function CollaborationColorSyncMount() {
    useCollaborationColorSync()
    return null
}

/**
 * Report whether the local user currently holds an editing lock in this room.
 *
 * Kept private to this module: it reads presence from inside the RoomProvider,
 * where `useMyPresence` is live, and lifts a single boolean up to the room host,
 * which sits above the provider and so cannot read presence itself. Reports
 * `false` on unmount so a room that is closing never leaves the connection pinned
 * open by a stale lock.
 */
function EditingLockKeepAliveProbe(props: EditingLockKeepAliveProbeProps) {
    const { onChange } = props
    const [presence] = useMyPresence()
    const isHoldingEditingLock = presence.editingFormId != null

    useEffect(() => {
        onChange(isHoldingEditingLock)
    }, [isHoldingEditingLock, onChange])

    useEffect(() => {
        return () => {
            onChange(false)
        }
    }, [onChange])

    return null
}

/**
 * Own the room's WebSocket by toggling it in place rather than by mounting or
 * unmounting the RoomProvider.
 *
 * The provider stays mounted across every idle, visibility, and availability
 * change so the page subtree it wraps keeps its React position and never
 * remounts (see the `CollaborationRoom` docstring). Opening and closing the
 * socket here, instead of conditionally rendering the provider, is what lets
 * that tree stay stable while the connection still comes and goes. Requires
 * `autoConnect={false}` on the provider so this is the sole owner of the socket.
 */
function RoomConnectionController(props: RoomConnectionControllerProps) {
    const { shouldConnect } = props
    const room = useRoom()

    useEffect(() => {
        if (shouldConnect) {
            room.connect()
        } else {
            room.disconnect()
        }
    }, [room, shouldConnect])

    return null
}

/**
 * Host the Liveblocks room for a resolved room id.
 *
 * Split out from the public component so that the availability subscription,
 * tab-activity listeners, and editing-lock probe only run once a room is
 * actually entered; a page that renders `CollaborationRoom` without a room name
 * mounts none of this. The rendered tree shape is invariant: the provider is
 * never conditionally unmounted, so `children` hold one React position through
 * every idle, visibility, and availability transition while
 * `RoomConnectionController` opens and closes the socket underneath them.
 */
function ConnectedCollaborationRoom(props: ConnectedCollaborationRoomProps) {
    const { children, roomId } = props

    const isTabActive = useTabActivity(HIDDEN_TAB_GRACE_MS, IDLE_TIMEOUT_MS)
    const [isHoldingEditingLock, setIsHoldingEditingLock] = useState(false)

    const [liveblocksAvailable, setLiveblocksAvailable] = useState(true)
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const reconnectAttemptsRef = useRef(0)

    useEffect(() => {
        // Subscribe to Liveblocks availability changes
        const unsubscribe = subscribeLiveblocksAvailability((available) => {
            if (!available) {
                // Liveblocks became unavailable
                setLiveblocksAvailable(false)

                // Clear any existing timeout
                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current)
                }

                // Try to reconnect on a fixed interval, up to the attempt cap
                if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
                    reconnectAttemptsRef.current += 1
                    reconnectTimeoutRef.current = setTimeout(() => {
                        console.log(
                            `Attempting to reconnect to Liveblocks... (Attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`,
                        )
                        setLiveblocksAvailable(true)
                    }, RECONNECT_INTERVAL_MS)
                } else {
                    console.warn("Max reconnection attempts reached. Liveblocks will remain disabled.")
                }
            } else {
                // Liveblocks is available again
                setLiveblocksAvailable(true)
                reconnectAttemptsRef.current = 0

                // Clear reconnect timeout if connection is restored
                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current)
                    reconnectTimeoutRef.current = null
                }
            }
        })

        return () => {
            unsubscribe()
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current)
            }
        }
    }, [])

    // A held editing lock pins the connection open even while hidden or idle; otherwise an
    // inactive tab leaves the room, which drops this user's presence for everyone else.
    const shouldConnect = liveblocksAvailable && (isTabActive || isHoldingEditingLock)

    return (
        <LiveblocksErrorBoundary fallback={children}>
            <RoomProvider id={roomId} initialPresence={{ online: true, editingFormId: null }} autoConnect={false}>
                <RoomConnectionController shouldConnect={shouldConnect} />
                <CollaborationColorSyncMount />
                <EditingLockKeepAliveProbe onChange={setIsHoldingEditingLock} />
                {children}
            </RoomProvider>
        </LiveblocksErrorBoundary>
    )
}

/**
 * Opt-in mount point for Liveblocks collaboration (live presence and editing locks).
 *
 * A Liveblocks room, and the WebSocket it opens, exists only for consumers that pass a
 * `roomName`. Omitting `roomName` (or rendering before the tenant id resolves) renders the
 * children untouched and never enters a room, so pages that do not collaborate pay nothing:
 * none of the availability, tab-activity, or presence machinery runs until a room is actually
 * entered. `roomName` is the company-unscoped name; the full Liveblocks room id is
 * `company-${serviceCompanyId}-${roomName}`, prepended here so a room can never collide across
 * tenants.
 *
 * Once entered, the room lives in `ConnectedCollaborationRoom`. The availability subscription
 * and optimistic reconnect live there rather than at each consumer because a room is entered in
 * exactly one place; centralizing the degraded-service handling keeps every page from
 * re-implementing the fallback, and lets the children render identically whether or not the
 * room is reachable. Every room also mounts one presence-color writer, so each page room and
 * object-scoped lock room gets exactly one color subscription by construction rather than
 * relying on a consumer to add it.
 *
 * The room disconnects on its own once the tab has been hidden for `HIDDEN_TAB_GRACE_MS` or the
 * user has been idle for `IDLE_TIMEOUT_MS`, and reconnects with no reload when the tab is shown
 * again or the user acts, so backgrounded tabs stop billing collaboration minutes. The one
 * exception is while the local user holds an editing lock (`editingFormId != null`): a held lock
 * signals likely-unsaved work, so the connection is pinned open to keep an alt-tab from
 * releasing the lock to someone else mid-edit.
 *
 * Those connect/disconnect cycles must never remount the page subtree. The RoomProvider stays
 * mounted for the whole life of the room and the socket is toggled underneath it at the
 * connection level (`autoConnect={false}` plus `RoomConnectionController`), so `children` keep
 * one React position across every idle, visibility, and availability change. Disconnecting by
 * unmounting the provider would instead tear down and rebuild everything it wraps — tables, open
 * dialogs, React Query caches, in-progress edit forms — on every transition, so the disconnect
 * is deliberately socket-level, not mount-level.
 */
export default function CollaborationRoom(props: CollaborationRoomProps) {
    const { children, roomName } = props
    const { user } = useUser()

    const companyId = user?.service_company?.id

    if (roomName == null || companyId == null) {
        return children
    }

    return (
        <ConnectedCollaborationRoom roomId={`company-${companyId}-${roomName}`}>{children}</ConnectedCollaborationRoom>
    )
}
